var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { LoadingButton } from '@mui/lab';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography, styled, } from '@mui/material';
import { gridClasses, } from '@mui/x-data-grid';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IntegrationType, PlanType } from '../../enum';
import { Autocomplete, ColumnFilterHeader, ConfirmDialog, DataGrid, DatePicker, ErrorMessageBox, FallbackComponent, FormField, useClientTableFilter, } from '../common';
import { getIntegrationTypeFromOptionId } from './constants';
import { useForecastBusinessUnitsAPI, useForecastGroupsAPI, useForecastImportAPI, useForecastListAPI, useForecastRoutingSetsAPI, useForecastRunsAPI, } from './selectors';
var StyledDataGrid = styled(DataGrid)(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {
            '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
                display: 'none',
            },
            '& .MuiDataGrid-overlay': {
                backgroundColor: 'unset',
            },
            '& .MuiDataGrid-selectedRowCount': {
                display: 'none',
            }
        },
        _b["& .".concat(gridClasses.columnHeaderCheckbox, " .").concat(gridClasses.columnHeaderTitleContainer, " .MuiCheckbox-root .MuiSvgIcon-root")] = {
            color: theme.palette.common.white,
        },
        _b);
});
var StyledDialogContent = styled(DialogContent)(function () { return ({
    minHeight: 'calc(100vh - 20rem)',
}); });
var ConfirmPlanDialog = function (_a) {
    var planId = _a.planId;
    var t = useTranslation('translation').t;
    var navigate = useNavigate();
    var handleConfirmPlan = function () { return navigate("/wizard/".concat(planId)); };
    return (_jsx(ConfirmDialog, { open: !!planId, onClose: handleConfirmPlan, message: t('confirmation.continuePlan'), cancelButtonText: "ok" }));
};
var useGenesysColumns = function (makeFilterHeader) {
    var t = useTranslation(['myPlans', 'translation']).t;
    return useMemo(function () { return [
        {
            field: 'weekDate',
            headerName: t('startDate'),
            flex: 1,
            sortable: false,
            renderHeader: makeFilterHeader('weekDate', t('startDate')),
            valueFormatter: function (params) {
                return moment(params.value).format(t('dateFormat.short', { ns: 'translation' }));
            },
        },
        {
            field: 'weekCount',
            headerName: t('numberOfWeeks'),
            flex: 1,
            sortable: false,
            renderHeader: makeFilterHeader('weekCount', t('numberOfWeeks')),
            valueGetter: function (params) { return params.value; },
        },
        {
            field: 'description',
            headerName: t('description'),
            flex: 1,
            sortable: false,
            renderHeader: makeFilterHeader('description', t('description')),
            valueFormatter: function (params) { return params.value; },
        },
        {
            field: 'dateCreated',
            headerName: t('dateCreated'),
            flex: 1,
            type: 'dateTime',
            sortable: false,
            renderHeader: makeFilterHeader('dateCreated', t('dateCreated')),
            valueGetter: function (params) { var _a; return (_a = params.row.metadata) === null || _a === void 0 ? void 0 : _a.dateCreated; },
            valueFormatter: function (params) {
                return moment(params.value).format(t('dateFormat.short', { ns: 'translation' }));
            },
        },
    ]; }, [t, makeFilterHeader]);
};
var useForecastGroupColumns = function (makeFilterHeader) {
    var t = useTranslation(['myPlans', 'translation']).t;
    return useMemo(function () { return [
        {
            field: 'code',
            headerName: t('forecastGroup'),
            flex: 1,
            sortable: false,
            renderHeader: makeFilterHeader('code', t('forecastGroup')),
        },
        {
            field: 'description',
            headerName: t('description'),
            flex: 2,
            sortable: false,
            renderHeader: makeFilterHeader('description', t('description')),
        },
    ]; }, [t, makeFilterHeader]);
};
var useForecastRunColumns = function (makeFilterHeader) {
    var t = useTranslation(['myPlans', 'translation']).t;
    return useMemo(function () { return [
        {
            field: 'name',
            headerName: t('forecastRun'),
            flex: 1.5,
            sortable: false,
            renderHeader: makeFilterHeader('name', t('forecastRun')),
        },
        {
            field: 'startDate',
            headerName: t('startDate'),
            flex: 1,
            sortable: false,
            renderHeader: makeFilterHeader('startDate', t('startDate')),
        },
        {
            field: 'stopDate',
            headerName: t('stopDate'),
            flex: 1,
            sortable: false,
            renderHeader: makeFilterHeader('stopDate', t('stopDate')),
        },
        {
            field: 'weeks',
            headerName: t('numberOfWeeks'),
            flex: 0.7,
            sortable: false,
            renderHeader: makeFilterHeader('weeks', t('numberOfWeeks')),
            // Aspect runs carry no week count; derive it from the inclusive start→stop span.
            valueGetter: function (params) {
                return params.row.startDate && params.row.stopDate
                    ? Math.round((moment(params.row.stopDate).diff(moment(params.row.startDate), 'days') + 1) / 7)
                    : '';
            },
        },
        {
            field: 'createdOn',
            headerName: t('dateCreated'),
            flex: 1,
            sortable: false,
            renderHeader: makeFilterHeader('createdOn', t('dateCreated')),
            valueFormatter: function (params) {
                return params.value ? moment(params.value).format(t('dateFormat.short', { ns: 'translation' })) : '';
            },
        },
        {
            field: 'memo',
            headerName: t('memo'),
            flex: 1,
            sortable: false,
            renderHeader: makeFilterHeader('memo', t('memo')),
        },
    ]; }, [t, makeFilterHeader]);
};
/**
 * Estimate how well an Aspect forecast run covers the plan, from the run's start/stop dates (day
 * granularity). Mirrors the backend rule that a period is filled only when the forecast fully spans
 * it, so the confirm dialog can tell the user which periods will import empty. The backend result is
 * timezone-aware and authoritative; this is a pre-import heads-up only, so a 1-day boundary case may
 * differ slightly from the final import. The run list only offers runs that overlap the plan window,
 * so a fully disjoint run can't be chosen and there is no separate no-overlap kind (a run covering
 * only part of the window yields 'partial').
 */
export function computePlanCoverage(planStart, periodLength, planType, run) {
    if (!planStart || !run)
        return undefined;
    var total = periodLength !== null && periodLength !== void 0 ? periodLength : 1;
    var isWeekly = planType === PlanType.Weekly;
    var step = isWeekly ? 'weeks' : 'months';
    var unit = isWeekly ? 'week' : 'month';
    // Zero-padded YYYY-MM-DD compares lexicographically, so string comparison is safe here.
    for (var i = 0; i < total; i++) {
        var periodStart = planStart.clone().add(i, step);
        var periodStop = periodStart.clone().add(1, step).subtract(1, 'day');
        var fullyCovered = run.startDate <= periodStart.format('YYYY-MM-DD') && run.stopDate >= periodStop.format('YYYY-MM-DD');
        if (!fullyCovered)
            return { kind: 'partial', unit: unit };
    }
    return { kind: 'full', unit: unit };
}
export var ForecastImportDialog = function (_a) {
    var _b, _c, _d, _e, _f;
    var planFormData = _a.planFormData, open = _a.open, onClose = _a.onClose, importType = _a.importType;
    var t = useTranslation(['myPlans', 'translation']).t;
    var navigate = useNavigate();
    var _g = useState(), exceedPlanId = _g[0], setExceedPlanId = _g[1];
    // Prefer the type carried on planFormData (multigeo connection flow); fall back to deriving it from
    // the option id for the embedded path, which has no per-connection planFormData.integrationType.
    var integrationType = useMemo(function () { var _a; return (_a = planFormData === null || planFormData === void 0 ? void 0 : planFormData.integrationType) !== null && _a !== void 0 ? _a : getIntegrationTypeFromOptionId(importType); }, [planFormData === null || planFormData === void 0 ? void 0 : planFormData.integrationType, importType]);
    var isAspect = integrationType === IntegrationType.Aspect;
    // Multigeo: integration credentials live on the selected client + named connection, so identify both
    // on every request. integrationId is absent for the embedded path (single implicit connection).
    var clientId = planFormData === null || planFormData === void 0 ? void 0 : planFormData.clientId;
    var integrationId = planFormData === null || planFormData === void 0 ? void 0 : planFormData.integrationId;
    var requestContext = useMemo(function () { return (__assign(__assign({}, (clientId != null && { clientId: clientId })), (integrationId != null && { integrationId: integrationId }))); }, [clientId, integrationId]);
    // Genesys data
    var _h = useForecastBusinessUnitsAPI(integrationType), businessUnitsData = _h[0], fetchBusinessUnitsData = _h[1], clearBusinessUnitsData = _h[2];
    var _j = useForecastListAPI(integrationType), forecastsData = _j[0], fetchForecastsData = _j[1], clearForecastsData = _j[2];
    // Aspect data
    var _k = useForecastRoutingSetsAPI(integrationType), routingSetsData = _k[0], fetchRoutingSetsData = _k[1], clearRoutingSetsData = _k[2];
    var _l = useForecastGroupsAPI(integrationType), forecastGroupsData = _l[0], fetchForecastGroupsData = _l[1], clearForecastGroupsData = _l[2];
    var _m = useForecastRunsAPI(integrationType), forecastRunsData = _m[0], fetchForecastRunsData = _m[1], clearForecastRunsData = _m[2];
    var _o = useForecastImportAPI(), importForecast = _o[0], fetchImportForecast = _o[1], clearImportForecast = _o[2];
    // Aspect body step: 1 = forecast groups (queues), 2 = forecast run. The routing set is a header
    // dropdown
    var _p = useState(1), aspectStep = _p[0], setAspectStep = _p[1];
    var _q = useState([]), selectedGroupIds = _q[0], setSelectedGroupIds = _q[1];
    var _r = useState(), selectedForecastRunId = _r[0], setSelectedForecastRunId = _r[1];
    var rowSelectionModel;
    if (aspectStep === 1) {
        rowSelectionModel = selectedGroupIds;
    }
    else if (selectedForecastRunId) {
        rowSelectionModel = [selectedForecastRunId];
    }
    else {
        rowSelectionModel = [];
    }
    // Genesys
    var _s = useState(), forecastId = _s[0], setForecastId = _s[1];
    var _t = useState(), importError = _t[0], setImportError = _t[1];
    var _u = useState(), coverageWarning = _u[0], setCoverageWarning = _u[1];
    var defaultValues = useMemo(function () {
        var _a, _b;
        var start = (_b = (_a = planFormData === null || planFormData === void 0 ? void 0 : planFormData.startDate) === null || _a === void 0 ? void 0 : _a.clone()) !== null && _b !== void 0 ? _b : moment.utc().startOf('d');
        return { forecastStartDate: start, businessUnitId: '', routingSetId: '' };
    }, [planFormData]);
    var _v = useState(), confirmDate = _v[0], setConfirmDate = _v[1];
    var _w = useForm({ defaultValues: defaultValues }), control = _w.control, handleSubmit = _w.handleSubmit, watch = _w.watch, reset = _w.reset;
    var _x = watch(), businessUnitId = _x.businessUnitId, routingSetId = _x.routingSetId, forecastStartDate = _x.forecastStartDate;
    // Routing set chosen from the header dropdown; carries the forecastGroupIds (queues) to hydrate.
    var selectedRoutingSet = useMemo(function () { var _a; return ((_a = routingSetsData.data) !== null && _a !== void 0 ? _a : []).find(function (r) { return r.id === routingSetId; }); }, [routingSetsData.data, routingSetId]);
    var routingSetGroupKey = (_b = selectedRoutingSet === null || selectedRoutingSet === void 0 ? void 0 : selectedRoutingSet.forecastGroupIds.join(',')) !== null && _b !== void 0 ? _b : '';
    useEffect(function () {
        if (!open)
            return;
        reset(defaultValues);
        setConfirmDate(defaultValues.forecastStartDate);
        setForecastId(undefined);
        setAspectStep(1);
        setSelectedGroupIds([]);
        setSelectedForecastRunId(undefined);
        setImportError(undefined);
        // Only re-initialize when the dialog opens. Including `reset`/`defaultValues` here can wipe
        // in-progress Aspect wizard state when those identities churn under load.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- open-only re-init
    }, [open]);
    // Genesys: load BUs on open
    useEffect(function () {
        if (!isAspect && integrationType) {
            fetchBusinessUnitsData(undefined, requestContext);
        }
        return clearBusinessUnitsData;
    }, [fetchBusinessUnitsData, clearBusinessUnitsData, integrationType, isAspect, requestContext]);
    // Aspect: load routing sets on open (populates the header dropdown)
    useEffect(function () {
        if (isAspect && integrationType) {
            fetchRoutingSetsData(undefined, requestContext);
        }
        return clearRoutingSetsData;
    }, [fetchRoutingSetsData, clearRoutingSetsData, integrationType, isAspect, requestContext]);
    // Aspect: hydrate forecast groups for the selected routing set.
    useEffect(function () {
        if (isAspect && routingSetGroupKey) {
            fetchForecastGroupsData(undefined, __assign({ forecastGroupIds: routingSetGroupKey }, requestContext));
        }
        return clearForecastGroupsData;
    }, [isAspect, routingSetGroupKey, requestContext, fetchForecastGroupsData, clearForecastGroupsData]);
    // Aspect: reset selection / step only when the routing set's group key actually changes
    // (not when fetch/clear callback identities churn).
    var prevRoutingSetGroupKeyRef = useRef(undefined);
    useEffect(function () {
        if (!isAspect)
            return;
        if (prevRoutingSetGroupKeyRef.current === routingSetGroupKey)
            return;
        var isInitial = prevRoutingSetGroupKeyRef.current === undefined;
        prevRoutingSetGroupKeyRef.current = routingSetGroupKey;
        if (isInitial)
            return;
        setSelectedGroupIds([]);
        setSelectedForecastRunId(undefined);
        setAspectStep(1);
    }, [isAspect, routingSetGroupKey]);
    // Genesys only: load forecasts when BU + date selected
    useEffect(function () {
        if (!isAspect && integrationType && confirmDate && businessUnitId) {
            fetchForecastsData(undefined, __assign({ weekDate: confirmDate.format('YYYY-MM-DD'), businessUnitId: businessUnitId }, requestContext));
        }
        return clearForecastsData;
    }, [fetchForecastsData, clearForecastsData, businessUnitId, confirmDate, integrationType, isAspect, requestContext]);
    // Aspect step 2: load forecast runs for the plan start date. The search is fixed to that date (not
    // user-editable) we want every run whose range includes it. The backend widens the query ±36mo,
    // and visibleForecastRuns then filters to the runs that actually contain the plan start date.
    useEffect(function () {
        if (isAspect && aspectStep === 2 && selectedRoutingSet && forecastStartDate) {
            var date = forecastStartDate.format('YYYY-MM-DD');
            fetchForecastRunsData(undefined, __assign({ routingSetId: selectedRoutingSet.id, startDate: date, stopDate: date }, requestContext));
        }
        return clearForecastRunsData;
    }, [
        fetchForecastRunsData,
        clearForecastRunsData,
        isAspect,
        aspectStep,
        selectedRoutingSet,
        forecastStartDate,
        requestContext,
    ]);
    // Genesys: single row selection
    var handleGenesysRowSelection = useCallback(function (ids) {
        var _a;
        setForecastId((_a = ids.at(-1)) !== null && _a !== void 0 ? _a : '');
        setImportError(undefined);
    }, []);
    // Aspect step 1: multi-select forecast groups
    var handleGroupSelection = useCallback(function (ids) {
        setSelectedGroupIds(ids);
        setImportError(undefined);
    }, []);
    // Aspect step 2: single forecast run selection
    var handleForecastRunSelection = useCallback(function (ids) {
        var _a;
        setSelectedForecastRunId((_a = ids.at(-1)) !== null && _a !== void 0 ? _a : undefined);
        setImportError(undefined);
    }, []);
    var handleAspectNext = useCallback(function () {
        setImportError(undefined);
        if (!selectedGroupIds.length) {
            setImportError({
                message: t('translation:exceptions.forecastNotFound', {
                    defaultValue: 'Please select at least one forecast group.',
                }),
            });
            return;
        }
        setSelectedForecastRunId(undefined);
        setAspectStep(2);
    }, [selectedGroupIds, t]);
    var handleAspectBack = useCallback(function () {
        setImportError(undefined);
        clearForecastRunsData();
        setAspectStep(1);
    }, [clearForecastRunsData]);
    useEffect(function () {
        var _a, _b, _c, _d, _e;
        if ((_a = importForecast === null || importForecast === void 0 ? void 0 : importForecast.data) === null || _a === void 0 ? void 0 : _a.exceedMax) {
            setExceedPlanId(String((_c = (_b = importForecast.data) === null || _b === void 0 ? void 0 : _b.plan) === null || _c === void 0 ? void 0 : _c.id));
        }
        else if ((_e = (_d = importForecast === null || importForecast === void 0 ? void 0 : importForecast.data) === null || _d === void 0 ? void 0 : _d.plan) === null || _e === void 0 ? void 0 : _e.id) {
            navigate("/wizard/".concat(importForecast.data.plan.id));
        }
        return clearImportForecast;
    }, [importForecast.data, clearImportForecast, navigate, open]);
    var debouncedSetConfirmDate = useMemo(function () { return debounce(function (date) { return date && setConfirmDate(date); }, 800); }, []);
    var handleDateAccept = function (date) { return date && setConfirmDate(date); };
    var handleDateChange = function (date) { return debouncedSetConfirmDate(date); };
    var handleImport = handleSubmit(function (_a) {
        var _b, _c;
        var startDateVal = _a.forecastStartDate;
        if (!planFormData) {
            setImportError({
                message: t('translation:exceptions.planDataMissing', {
                    defaultValue: 'Plan data is missing. Please close and try again.',
                }),
            });
            return;
        }
        if (!integrationType) {
            setImportError({
                message: t('translation:exceptions.integrationTypeInvalid', {
                    defaultValue: 'Invalid integration type. Please close and try again.',
                }),
            });
            return;
        }
        if (isAspect) {
            if (!selectedForecastRunId || !selectedGroupIds.length || !selectedRoutingSet) {
                setImportError({
                    message: t('translation:exceptions.forecastNotFound', {
                        defaultValue: 'Please choose a routing set, forecast group(s) and a forecast before importing.',
                    }),
                });
                return;
            }
            setImportError(undefined);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            var _importData_1 = planFormData.importData, startDate_1 = planFormData.startDate, rest_1 = __rest(planFormData, ["importData", "startDate"]);
            var payload = __assign(__assign({}, rest_1), { startDate: startDate_1.format('YYYY-MM-DD'), forecastId: selectedForecastRunId, routingSetId: selectedRoutingSet.id, forecastGroupIds: selectedGroupIds, weekDate: startDateVal.format('YYYY-MM-DD'), integrationType: integrationType });
            // Every selectable run contains the plan start date, but it may still end before the plan
            // does. Those import with some periods empty (the coverage guard blanks any period the
            // forecast doesn't fully span), so warn before proceeding. Full coverage imports straight
            // through.
            var run = ((_b = forecastRunsData.data) !== null && _b !== void 0 ? _b : []).find(function (r) { return r.id === selectedForecastRunId; });
            var coverage = computePlanCoverage(planFormData.startDate, planFormData.periodLength, planFormData.planType, run);
            if (coverage && coverage.kind !== 'full') {
                setCoverageWarning({ payload: payload, coverage: coverage });
                return;
            }
            fetchImportForecast(payload);
            return;
        }
        var currentForecastData = ((_c = forecastsData.data) !== null && _c !== void 0 ? _c : []).find(function (row) { return row.id === forecastId; });
        if (!currentForecastData) {
            setImportError({
                message: t('translation:exceptions.forecastNotFound', {
                    defaultValue: 'Selected forecast not found. Please select a forecast from the list.',
                }),
            });
            return;
        }
        setImportError(undefined);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        var _importData = planFormData.importData, startDate = planFormData.startDate, rest = __rest(planFormData, ["importData", "startDate"]);
        fetchImportForecast(__assign(__assign({}, rest), { startDate: startDate.format('YYYY-MM-DD'), forecastId: currentForecastData.id, weekDate: currentForecastData.weekDate, businessUnitId: businessUnitId, integrationType: integrationType }));
    });
    useEffect(function () { return function () { return debouncedSetConfirmDate.cancel(); }; }, [debouncedSetConfirmDate]);
    var aspectStepTitle = function () { return (aspectStep === 1 ? t('selectForecastGroups') : t('selectForecastRun')); };
    var planWindow = useMemo(function () {
        var _a;
        if (!forecastStartDate)
            return undefined;
        var isWeekly = (planFormData === null || planFormData === void 0 ? void 0 : planFormData.planType) === PlanType.Weekly;
        var start = forecastStartDate.clone();
        var stop = start
            .clone()
            .add((_a = planFormData === null || planFormData === void 0 ? void 0 : planFormData.periodLength) !== null && _a !== void 0 ? _a : 1, isWeekly ? 'weeks' : 'months')
            .subtract(1, 'day');
        return { start: start, stop: stop };
    }, [forecastStartDate, planFormData === null || planFormData === void 0 ? void 0 : planFormData.periodLength, planFormData === null || planFormData === void 0 ? void 0 : planFormData.planType]);
    // Aspect widens the run query ±36mo, so the API returns far more than asked for. Show every run
    // whose date range OVERLAPS the plan window [start, stop] — including runs that cover only part of
    // it. On import clipAndPad fills whatever the run covers and pads the rest with
    // holes, and computePlanCoverage warns about any gaps. Most-recent first.
    var visibleForecastRuns = useMemo(function () {
        var _a;
        var runs = (_a = forecastRunsData.data) !== null && _a !== void 0 ? _a : [];
        if (!planWindow)
            return runs;
        var planStart = planWindow.start.format('YYYY-MM-DD');
        var planStop = planWindow.stop.format('YYYY-MM-DD');
        return runs
            .filter(function (r) { return r.startDate <= planStop && r.stopDate >= planStart; })
            .sort(function (a, b) { return b.startDate.localeCompare(a.startDate); });
    }, [forecastRunsData.data, planWindow]);
    var genesysFilterColumns = useMemo(function () { return [
        { field: 'weekDate', filter: 'date', sortable: true },
        { field: 'weekCount', sortable: true },
        { field: 'description', filter: 'text', sortable: true },
        { field: 'dateCreated', filter: 'date', sortable: true },
    ]; }, []);
    var groupFilterColumns = useMemo(function () { return [
        { field: 'code', filter: 'text', sortable: true },
        { field: 'description', filter: 'text', sortable: true },
    ]; }, []);
    var runFilterColumns = useMemo(function () { return [
        { field: 'name', filter: 'text', sortable: true },
        { field: 'startDate', filter: 'date', sortable: true },
        { field: 'stopDate', filter: 'date', sortable: true },
        { field: 'weeks', sortable: true },
        { field: 'createdOn', filter: 'date', sortable: true },
        { field: 'memo', filter: 'text', sortable: true },
    ]; }, []);
    var getGenesysValue = useCallback(function (row, field) {
        var _a;
        if (field === 'dateCreated')
            return (_a = row.metadata) === null || _a === void 0 ? void 0 : _a.dateCreated;
        return undefined;
    }, []);
    var getRunValue = useCallback(function (row, field) {
        if (field === 'weeks') {
            return row.startDate && row.stopDate
                ? Math.round((moment(row.stopDate).diff(moment(row.startDate), 'days') + 1) / 7)
                : '';
        }
        return undefined;
    }, []);
    var _y = useClientTableFilter({
        rows: (_c = forecastsData.data) !== null && _c !== void 0 ? _c : [],
        columns: genesysFilterColumns,
        getValue: getGenesysValue,
    }), processedGenesysRows = _y.processedRows, getGenesysFilterState = _y.getFilterState, getGenesysAllValues = _y.getAllValues, applyGenesysFilter = _y.applyFilter;
    var _z = useClientTableFilter({
        rows: (_d = forecastGroupsData.data) !== null && _d !== void 0 ? _d : [],
        columns: groupFilterColumns,
    }), processedGroupRows = _z.processedRows, getGroupFilterState = _z.getFilterState, getGroupAllValues = _z.getAllValues, applyGroupFilter = _z.applyFilter;
    var _0 = useClientTableFilter({
        rows: visibleForecastRuns,
        columns: runFilterColumns,
        getValue: getRunValue,
    }), processedRunRows = _0.processedRows, getRunFilterState = _0.getFilterState, getRunAllValues = _0.getAllValues, applyRunFilter = _0.applyFilter;
    var makeGenesysFilterHeader = useCallback(function (field, label) { return function () { return (_jsx(ColumnFilterHeader, { label: label, filterState: getGenesysFilterState(field), allValues: getGenesysAllValues(field), onApply: function (update) { return applyGenesysFilter(field, update); } })); }; }, [applyGenesysFilter, getGenesysAllValues, getGenesysFilterState]);
    var makeGroupFilterHeader = useCallback(function (field, label) { return function () { return (_jsx(ColumnFilterHeader, { label: label, filterState: getGroupFilterState(field), allValues: getGroupAllValues(field), onApply: function (update) { return applyGroupFilter(field, update); } })); }; }, [applyGroupFilter, getGroupAllValues, getGroupFilterState]);
    var makeRunFilterHeader = useCallback(function (field, label) { return function () { return (_jsx(ColumnFilterHeader, { label: label, filterState: getRunFilterState(field), allValues: getRunAllValues(field), onApply: function (update) { return applyRunFilter(field, update); } })); }; }, [applyRunFilter, getRunAllValues, getRunFilterState]);
    var genesysColumns = useGenesysColumns(makeGenesysFilterHeader);
    var forecastGroupColumns = useForecastGroupColumns(makeGroupFilterHeader);
    var forecastRunColumns = useForecastRunColumns(makeRunFilterHeader);
    var aspectGridLoading = aspectStep === 1 ? routingSetsData.requesting || forecastGroupsData.requesting : forecastRunsData.requesting;
    var aspectGridRows = aspectStep === 1 ? processedGroupRows : processedRunRows;
    var aspectGridColumns = aspectStep === 1 ? forecastGroupColumns : forecastRunColumns;
    var aspectErrors = [
        routingSetsData.error,
        forecastGroupsData.error,
        forecastRunsData.error,
        importForecast.error,
        importError,
    ];
    var genesysImportDisabled = !isAspect && !forecastId;
    var aspectImportDisabled = isAspect && (!selectedForecastRunId || !selectedGroupIds.length);
    // Warns that not every plan period is fully covered by the chosen run, so some work volume /
    // handle time must still be filled in by hand — forecast groups, arrival pattern, hours of
    // operation and service-level targets always import.
    var coverageMessage = useMemo(function () {
        if (!coverageWarning)
            return '';
        var unit = coverageWarning.coverage.unit;
        // Localize the unit noun so the sentence reads naturally in every language.
        var unitSingular = t("coverage.unit.".concat(unit), { defaultValue: unit });
        return t('coverage.partial', {
            defaultValue: 'This forecast does not fully cover every {{unit}} within the plan, so work volume and/or handle time will still need to be completed. Continue?',
            unit: unitSingular,
        });
    }, [coverageWarning, t]);
    return (_jsxs(_Fragment, { children: [_jsx(ConfirmPlanDialog, { planId: exceedPlanId }), _jsx(ConfirmDialog, { open: !!coverageWarning, onClose: function () { return setCoverageWarning(undefined); }, title: t('translation:confirmation.title'), confirmButtonText: "import", message: coverageMessage, onConfirm: function () {
                    if (coverageWarning)
                        fetchImportForecast(coverageWarning.payload);
                    setCoverageWarning(undefined);
                } }), _jsx(Dialog, __assign({ maxWidth: "lg", open: open, onClose: onClose }, { children: _jsxs(Box, __assign({ component: "form", onSubmit: handleImport, "data-testid": "importForecastDialog" }, { children: [_jsx(DialogTitle, __assign({ style: { display: 'flex', alignItems: 'center' } }, { children: _jsxs(Stack, __assign({ direction: 'row', alignItems: 'center', justifyContent: "space-between", width: "100%" }, { children: [_jsx(Typography, __assign({ variant: "h2" }, { children: isAspect ? aspectStepTitle() : t('selectForecast') })), !isAspect && (_jsxs(Stack, __assign({ direction: 'row', gap: 10, alignItems: 'center' }, { children: [_jsxs(Stack, __assign({ direction: 'row', alignItems: 'center', gap: 2 }, { children: [_jsx(Typography, __assign({ variant: "h3" }, { children: t('myPlans:forecastStartDate') })), _jsx(FormField, __assign({ name: "forecastStartDate", control: control, width: "10rem", "data-testid": "forecastStartDate" }, { children: _jsx(DatePicker, { control: control, onAccept: handleDateAccept, onChange: handleDateChange, "data-testid": "datePicker" }) }))] })), _jsxs(Stack, __assign({ direction: 'row', alignItems: 'center', gap: 2 }, { children: [_jsx(Typography, __assign({ variant: "h3" }, { children: t('businessUnit') })), _jsx(FormField, __assign({ name: "businessUnitId", control: control, minWidth: "12.5rem" }, { children: _jsx(Autocomplete, { options: (_e = businessUnitsData.data) !== null && _e !== void 0 ? _e : [], placeholder: t('selectBusinessUnit'), value: businessUnitId }) }))] }))] }))), isAspect && aspectStep === 1 && (_jsxs(Stack, __assign({ direction: 'row', alignItems: 'center', gap: 2 }, { children: [_jsx(Typography, __assign({ variant: "h3" }, { children: t('routingSet') })), _jsx(FormField, __assign({ name: "routingSetId", control: control, minWidth: "12.5rem" }, { children: _jsx(Autocomplete, { options: (_f = routingSetsData.data) !== null && _f !== void 0 ? _f : [], placeholder: routingSetsData.requesting ? t('loadingRoutingSets') : t('selectRoutingSet'), value: routingSetId, loading: routingSetsData.requesting, disabled: routingSetsData.requesting }) }))] }))), isAspect && aspectStep === 2 && (_jsx(Typography, __assign({ variant: "body1", "data-testid": "forecastSearchDate" }, { children: t('listOfForecastsForPlanWindow', {
                                            defaultValue: 'List of forecasts for the plan {{start}} – {{stop}}',
                                            // Normalize to the active locale: these moments carry their own (possibly stale)
                                            // locale, so without this the month renders in the wrong language (e.g. "juil.").
                                            start: planWindow === null || planWindow === void 0 ? void 0 : planWindow.start.clone().locale(moment.locale()).format(t('dateFormat.short', { ns: 'translation' })),
                                            stop: planWindow === null || planWindow === void 0 ? void 0 : planWindow.stop.clone().locale(moment.locale()).format(t('dateFormat.short', { ns: 'translation' })),
                                        }) })))] })) })), _jsx(StyledDialogContent, { children: _jsx(FallbackComponent, __assign({ overlay: true }, { children: isAspect ? (_jsxs(_Fragment, { children: [_jsx(ErrorMessageBox, { errors: aspectErrors }), _jsx(StyledDataGrid, { loading: aspectGridLoading, columns: aspectGridColumns, rows: aspectGridRows, autoHeight: true, pageSize: 10, checkboxSelection: true, sx: aspectStep === 1
                                                ? {
                                                    '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
                                                        display: 'flex',
                                                    },
                                                }
                                                : undefined, onRowSelectionModelChange: aspectStep === 1 ? handleGroupSelection : handleForecastRunSelection, rowSelectionModel: rowSelectionModel })] })) : (_jsxs(_Fragment, { children: [_jsx(ErrorMessageBox, { errors: [forecastsData.error, importForecast.error, businessUnitsData.error, importError] }), _jsx(StyledDataGrid, { loading: forecastsData.requesting, columns: genesysColumns, rows: processedGenesysRows, autoHeight: true, pageSize: 10, checkboxSelection: true, onRowSelectionModelChange: handleGenesysRowSelection, rowSelectionModel: forecastId })] })) })) }), _jsxs(DialogActions, __assign({ sx: { justifyContent: 'space-between' } }, { children: [_jsx(Button, __assign({ variant: "outlined", onClick: onClose }, { children: t('translation:action.cancel') })), _jsxs(Stack, __assign({ direction: "row", spacing: 2 }, { children: [isAspect && aspectStep === 2 && (_jsx(Button, __assign({ variant: "outlined", onClick: handleAspectBack }, { children: t('translation:action.back', { defaultValue: 'Back' }) }))), isAspect && aspectStep === 1 ? (_jsx(Button, __assign({ variant: "contained", onClick: handleAspectNext, disabled: !selectedGroupIds.length }, { children: t('translation:action.next', { defaultValue: 'Next' }) }))) : (_jsx(LoadingButton, __assign({ loading: importForecast.requesting, variant: "contained", disabled: aspectImportDisabled || genesysImportDisabled, onClick: handleImport }, { children: t('translation:action.import') })))] }))] }))] })) }))] }));
};
